import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';
import type { Brand, Location } from '@traaker/shared';

interface BrandState {
  brands: Brand[];
  activeBrand: Brand | null;
  activeLocation: Location | null;
  loading: boolean;
  setActiveBrand: (brand: Brand) => void;
  setActiveLocation: (location: Location | null) => void;
  refreshBrands: () => Promise<void>;
}

const BrandContext = createContext<BrandState>({
  brands: [],
  activeBrand: null,
  activeLocation: null,
  loading: true,
  setActiveBrand: () => {},
  setActiveLocation: () => {},
  refreshBrands: async () => {},
});

const ACTIVE_BRAND_STORAGE_KEY = 'traaker.activeBrandId';

export function BrandProvider({ children }: { children: ReactNode }) {
  const { tenant } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [activeBrand, setActiveBrand] = useState<Brand | null>(null);
  const [activeLocation, setActiveLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(true);

  const loadBrands = useCallback(async () => {
    if (!tenant) {
      // No tenant resolved yet — we haven't loaded brands, so stay in the
      // loading state rather than reporting "loaded, zero brands". Otherwise
      // OnboardingGate would see loading=false + brands=[] during the
      // auth-resolution window and wrongly redirect a tenant that HAS brands.
      return;
    }
    try {
      const data = await api.get<Brand[]>('/brands');
      setBrands(data);
      // Re-bind the active brand to the fresh record so callers that trigger
      // refreshBrands (e.g. after validate / save strategy) see the new
      // strategyMeta / geoStrategyMeta values. On first load (activeBrand
      // is null) restore from localStorage so a page refresh keeps the
      // user's selection instead of snapping to data[0].
      if (data.length > 0) {
        const storedId =
          typeof window !== 'undefined'
            ? window.localStorage.getItem(ACTIVE_BRAND_STORAGE_KEY)
            : null;
        const fresh =
          (activeBrand && data.find((b) => b.brandId === activeBrand.brandId)) ||
          (storedId ? data.find((b) => b.brandId === storedId) : undefined);
        if (fresh) {
          setActiveBrand(fresh);
        } else {
          setActiveBrand(data[0]);
          setActiveLocation(null);
        }
      } else {
        setActiveBrand(null);
        setActiveLocation(null);
      }
    } catch {
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, [tenant, activeBrand]);

  useEffect(() => {
    loadBrands();
  }, [tenant]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetActiveBrand = useCallback((brand: Brand) => {
    setActiveBrand(brand);
    setActiveLocation(null); // reset location on brand switch
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ACTIVE_BRAND_STORAGE_KEY, brand.brandId);
    }
  }, []);

  return (
    <BrandContext.Provider
      value={{
        brands,
        activeBrand,
        activeLocation,
        loading,
        setActiveBrand: handleSetActiveBrand,
        setActiveLocation,
        refreshBrands: loadBrands,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}
